import { CommonModule, CurrencyPipe, formatDate, TitleCasePipe } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, filter, from, Observable, take } from 'rxjs';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { Logger, LogLevel } from '../../../infrastructure/logging/logger';
import { ColorProp, ColorPropType, getColor } from '../../../layout/Colors';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { PillComponent } from '../../../shared/components/pill/pill.component';
import { dateFormat } from '../../../utilities/DateUtility';
import { NotificationService } from '../../../utilities/PortalNotificationUtility';
import { ClaimDetailDateDisplayComponent } from '../../claims/claim-detail/components/claim-detail-date-display/claim-detail-date-display.component';
import { claimStatusIdToStatus, getClaimStatusColor } from '../../claims/ClaimHelper';
import { Claim } from '../../claims/models/Claim';
import { ClaimsService } from '../../claims/services/claims.service';
import { GuestDetails, ReservationGuestDetail, VerificationResultDetail } from '../../guests/models/GuestDetails';
import { GuestService } from '../../guests/services/guest.service';
import { OrganizationService } from '../../organizations/services/organizations.service';
import { PropertyHelper } from '../../properties/shared/PropertyHelper';
import { UpdateReservationStatusDialogComponent } from '../components/update-reservation-status-dialog/update-reservation-status-dialog.component';
import { Reservation, ReservationSelectedPolicy } from '../models/Reservation';
import { ReservationHistory } from '../models/ReservationHistory';
import { getFormattedReservationStatus, getLegacyReservationStatus, getReservationStatusPillColor, getVerificationStatusPillColor } from '../reservation-detail/ReservationHelper';
import { ReservationService } from '../services/reservation.service';
import { CancelReservationConfirmationDialogComponent } from './components/cancel-reservation-confirmation-dialog/cancel-reservation-confirmation-dialog.component';
import { ReservationDetailVerificationStatusRowComponent } from './components/reservation-detail-verification-status-row/reservation-detail-verification-status-row.component';
import { LegacyReservationStatus } from './ReservationDetailConstants';

@Component({
  selector: 'app-reservation-detail',
  templateUrl: './reservation-detail.component.html',
  styleUrls: ['./reservation-detail.component.scss'],
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    MatIconModule,
    ClaimDetailDateDisplayComponent,
    MatCardModule,
    PillComponent,
    CurrencyPipe,
    ReservationDetailVerificationStatusRowComponent,
    MatTableModule,
    TitleCasePipe,
    CommonModule,
    MatButtonModule,
    BackButtonComponent,
    RouterModule
  ]
})
export class ReservationDetailComponent implements OnInit {

  @ViewChild('photoModal') photoModal!: ElementRef;

  loading = new BehaviorSubject<boolean>(false);
  loadingReservationDetails = true;
  loadingVerificationDetails = true;
  loadingClaimHistory = true;
  loadingReservationHistory = true;
  displayNewClaimButton = false;
  displayNewClaimWarning = false;
  displayVerificationCard = true;
  displayCancelReservationButton = true;
  editReservationEnabled: boolean = true;
  portalAccount: any;

  reservationId!: number;
  reservation!: Reservation;
  guestDetails?: GuestDetails;
  noGuestBirthdayProvided: boolean = false;
  reservationGuestDetails?: ReservationGuestDetail;
  verificationResultDetails?: VerificationResultDetail[];

  identityVerificationResult?: VerificationResultDetail;
  priorRentalGuestVerificationResult?: VerificationResultDetail;
  criminalVerificationResult?: VerificationResultDetail;
  watchListVerificationResult?: VerificationResultDetail;
  sexOffenderVerificationResult?: VerificationResultDetail;

  insurancePolicy?: ReservationSelectedPolicy;
  insurancePolicies: any[] = [];
  totalInsuranceCost: number = 0;

  latestClaimId?: string;
  claims?: Claim[];
  claimHistoryDataSource = new MatTableDataSource<any>();
  claimHistoryColumns = ['claimId', 'dateSubmitted', 'status', 'claimDetails'];
  claimHistoryCount = 0;

  formattedPropertyAddress$?: Observable<string>;
  propertyAddress?: string;
  propertyType?: string;
  propertyPhotos?: string[];
  modalPropertyPhoto?: string;

  reservationHistory?: ReservationHistory[];
  reservationHistoryDataSource = new MatTableDataSource<any>();
  reservationHistoryColumns = ['timestamp', 'status', 'arrivalDate', 'departureDate', 'numberOfNights', 'insuranceCost'];

  reservationDetailsBookingDate: string = '';
  reservationDetailsArrivalDate: string = '';
  reservationDetailsDepartureDate: string = '';

  getReservationStatus = getLegacyReservationStatus;
  getFormattedReservationStatus = getFormattedReservationStatus;
  getVerificationStatusPillColor = getVerificationStatusPillColor;
  getClaimStatusColor = getClaimStatusColor;
  getReservationStatusPillColor = getReservationStatusPillColor;
  dateFormat = dateFormat;

  getColor = getColor;
  colorProp = ColorProp;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reservationService: ReservationService,
    private guestService: GuestService,
    private claimService: ClaimsService,
    private organizationService: OrganizationService,
    private dialogRef: MatDialog,
    public userContextProvider: UserContextProvider,
    private notificationService: NotificationService,
    private logService: Logger,
    private readonly propertyHelper: PropertyHelper
  ) { }

  ngOnInit(): void {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.route.params.subscribe(params => {
        this.reservationId = params['id'];
        this.load();
      });
    });
  }

  @HostListener('document:click', ['$event'])
  clickout(event: any): void {
    if(!this.modalPropertyPhoto) {
      return;
    }

    if(!this.photoModal.nativeElement.contains(event.target)) {
      this.modalPropertyPhoto = '';
    }
  }

  async load() {
    try {
      this.loading.next(true);
      this.editReservationEnabled = true;

      await this.loadReservationById();
      await Promise.all([
        this.loadOrganization(),
        this.loadClaimHistory(),
        this.loadGuestDetails()
      ]);
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while retrieving Reservation details', 'Error while retrieving Reservation details');
    } finally {
      this.loading.next(false);
    }
  }

  async loadReservationById() {
    try {
      this.reservation = await this.reservationService.getReservationById(this.reservationId);

      this.reservationDetailsBookingDate = dateFormat(this.reservation.booking_date?.toString(), this.userContextProvider.region);
      this.reservationDetailsArrivalDate = dateFormat(this.reservation.arrival_date?.toString(), this.userContextProvider.region);
      this.reservationDetailsDepartureDate = dateFormat(this.reservation.departure_date?.toString(), this.userContextProvider.region);

      this.insurancePolicy = this.reservation?.selected_policies?.[0];
      this.propertyType = this.getPropertyTypeFromId(this.reservation?.property?.property_type_id!);
      this.propertyPhotos = this.reservation?.property?.photos?.map(photo => photo.url);

      this.displayNewClaimButton = this.canFileClaimForReservation(this.reservation);
      this.displayCancelReservationButton = this.getReservationStatus(this.reservation.status) == LegacyReservationStatus.UPCOMING;

      this.formattedPropertyAddress$ = from(this.propertyHelper.getFormattedPropertyAddress(this.reservation.property));

      await this.loadReservationHistory();
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while retrieving Reservation details', 'Error while retrieving Reservation details');
    } finally {
      this.loadingReservationDetails = false;
    }
  }

  canFileClaimForReservation(reservation: Reservation): boolean {
    const status = getLegacyReservationStatus(reservation);

    if (status === 5) {
      this.logService.handleError(`Unable to determine reservation status for reservation ${reservation.reservation_id}`, `Unable to determine reservation status`, `Unable to determine reservation status for reservation ${reservation.reservation_id}`);

      return false;
    }
    
    const reservationComplete = status == LegacyReservationStatus.COMPLETE;
    const guestIsInHome = status == LegacyReservationStatus.IN_HOME;
    const claimAlreadyFiled = status == LegacyReservationStatus.CLAIM_FILED;
    const reservationStatusAllowsNewClaim = reservationComplete || guestIsInHome || claimAlreadyFiled;

    return reservationStatusAllowsNewClaim;
  }

  async loadReservationHistory() {
    try {
      const reservationHistory = await this.reservationService.getReservationHistory(this.reservationId);
      this.reservationHistoryDataSource.data = reservationHistory.map(history => {
        let arrivalDate = '';
        let departureDate = '';
        try {
          arrivalDate = formatDate(history.arrival_date, 'MMM d, y', 'en-US', 'UTC');
          departureDate = formatDate(history.departure_date, 'MMM d, y', 'en-US', 'UTC');
        } catch (e) {
          this.logService.handleError(e, `Unable to convert Reservation history Arrival or Departure Date`, `Unable to convert Reservation history Arrival Date: ${history.arrival_date} or Departure Date: ${history.departure_date} to a date`);
        }

        const dateObject = new Date(history.updated_date);
        const localizedTimeString = dateObject.toLocaleTimeString();

        return {
          updatedDate: history.updated_date,
          updatedTime: localizedTimeString,
          status: history.status,
          arrivalDate: arrivalDate,
          departureDate: departureDate,
          numberOfNights: history.number_of_nights,
          insuranceCost: history.insurance_cost
        }
      });
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while retrieving Reservation history', 'Error while retrieving Reservation history');
    } finally {
      this.loadingReservationHistory = false;
    }
  }
  
  async loadClaimHistory() {
    try {
      const claims = (await this.claimService.getClaims({ 
        filter: { draft: true, submitted: true, completed: true },
        page: 1, 
        pageSize: 100, 
        reservationId: this.reservationId
      })).claims;
      
      if (claims) {
        this.claimHistoryDataSource.data = claims.map(claim => {
          const sortedHistory = claim.status_history.sort((a, b) => new Date(b.status_date).getTime() - new Date(a.status_date).getTime());
          return {
            claimId: claim.id,
            dateSubmitted: claim.created_date,
            status: claimStatusIdToStatus(sortedHistory?.[0].status_id),
            backgroundColor: getClaimStatusColor(sortedHistory?.[0].status_id)
          }
        });
        if (this.userContextProvider.isNaUser()) {
          this.latestClaimId = claims[0]?.id;
        }
        this.claimHistoryCount = this.claimHistoryDataSource.data.length;
        this.loadingClaimHistory = false;
      }
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while retrieving Claim history', 'Error while retrieving Claim history');
      this.loadingClaimHistory = false;
    }
  }

  async loadGuestDetails() {
    try {
      if (!this.reservation.guests || this.reservation.guests.length === 0) {
        this.logService.handleError(`No guests found for reservation ${this.reservationId}`, `No guests found for reservation`, `No guests found for reservation ${this.reservationId}`);
        return;
      } else {
        this.guestDetails = await this.guestService.getGuestDetails(this.reservation.guests[0].guest_id);

        if (!this.guestDetails.birth_date || this.guestDetails.birth_date === new Date("0000-00-00 00:00:00")) {
          this.noGuestBirthdayProvided = true;
        }

        this.reservationGuestDetails = this.guestDetails.reservation_guest_details.find(detail => detail.reservation_id == this.reservationId);
        this.verificationResultDetails = this.guestDetails.verification_result_details.filter(verificationResult => verificationResult.reservation_guest_id === this.reservationGuestDetails?.id);
        this.populateVerificationResults();
      }
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while retrieving Guest details', 'Error while retrieving Guest details');
    } finally {
      this.loadingVerificationDetails = false;
    }
  }

  async loadOrganization() {
    const organization = await this.organizationService.getOrganizationDetail(this.reservation.organization_id);
    if (organization == null) {
      return;
    }

    // this.displayVerificationCard = organization.for_guest_verification == 1;
  }

  getFormattedAddress() {
    let formattedAddress: string = '';
    
    if(!this.guestDetails || !this.guestDetails.address) {
      this.logService.log({ 
        message: `Guest Details not loaded yet`, 
        level: LogLevel.DEBUG
      });
      return formattedAddress;
    }

    let address = this.guestDetails.address;
    formattedAddress = `${address.address_line_1 || ''} ${address.city || ''} ${address.state || ''} ${address.postal_code || ''}`

    return formattedAddress;
  }

  populateVerificationResults(): void {
    const findResult = (checkType: string): VerificationResultDetail | undefined => {
      return this.verificationResultDetails?.find(result => result.check_type === checkType);
    }

    this.identityVerificationResult = findResult('IDENTITY');
    this.priorRentalGuestVerificationResult = findResult('BAD GUEST');
    this.criminalVerificationResult = findResult('NATIONAL CRIMINAL');
    this.watchListVerificationResult = findResult('WATCHLIST');
    this.sexOffenderVerificationResult = findResult('SEX OFFENDER');
  }

  onStartClaimClick(): void {
    this.initiateNewClaim(this.reservationId);
  }

  private initiateNewClaim(reservationId: number) {
    this.router.navigate(['/claims/form'], { queryParams: { reservationId } });
  }

  onGuestVerificationPdfDownloadClick(): void {
    try {
      const guestId = this.guestDetails?.guest_id;
      this.guestService.getGuestVerificationPdf(guestId, this.reservationId);
    } catch (e: unknown) {
      if (e instanceof Error) {
        this.logService.handleError(e, 'An error occurred while downloading PDF', 'Error while downloading Guest Verification PDF');
      }
    }
  }

  onReservationGuestCoveragePdfDownloadClick(): void {
    this.reservationService.getReservationCoverageReport(this.reservationId, 1);
  }

  onReservationHomeownerCoveragePdfDownloadClick(): void {
    this.reservationService.getReservationCoverageReport(this.reservationId, 2);
  }

  onClaimClick(row: any): void {
    this.router.navigate([`/claims/detail/${row.claimId}`]);
  }

  onEditReservationClick() {
    this.router.navigate(['/reservations/form'], { queryParams: { reservationId: this.reservationId } });
  }

  getPropertyTypeFromId = (typeId: number): string => {
    switch (typeId) {
      case 1:
        return "Apartment";
      case 2:
        return "Townhouse";
      case 3:
        return "Single Family House";
      case 4:
        return "Hotel";
      case 5:
        return "Others";
      default:
        return "";
    }
  }

  getVerificationStatusText = (reservationGuestDetails?: ReservationGuestDetail): string => {
    return reservationGuestDetails?.verification_status ?? '';
  }

  getNewClaimButtonText() {
    if (this.latestClaimId) {
      return `Claim #${this.latestClaimId}`;
    } else {
      return `+ New Claim`;
    }
  }

  onNewClaimButtonClicked() {
    if (this.latestClaimId) {
      this.router.navigate([`/claims/detail/${this.latestClaimId}`]);
    } else {
      this.onStartClaimClick();
    }
  }

  onViewReservationPropertyClick() {
    this.router.navigate([`/properties/detail/${ this.reservation.property.property_id }`]);
  }

  onViewReservationOrganizationClick() {
    this.router.navigate([`/organizations/detail/${ this.reservation.organization_id }`]);
  }

  onViewPolicyDetailClick() {
    this.router.navigate([`/policies/detail/${ this.reservation.selected_policies[0].policy.id }`]);
  }

  onCancelReservationClick() {
    this.dialogRef.open(CancelReservationConfirmationDialogComponent, {
      width: '500px',
      data: {
        reservationId: this.reservationId,
        createdDate: this.reservation.created_date,
        cancelDate: new Date()
      }
    }).afterClosed().subscribe(async (cancel: boolean) => {
      if(!cancel) {
        return;
      }

      try {
        const reservationCanceled = await this.cancelReservation(this.reservationId.toString(), new Date());
        const invoiceStatusUpdated = await this.updateReservationInvoiceStatusAfterCancelation(this.reservationId.toString());
        if (reservationCanceled && invoiceStatusUpdated) {
          this.notificationService.showNotification({ message: 'Reservation has been successfully canceled'});
        }
      } catch (e) {
        this.logService.handleError(e, 'An error occurred while canceling Reservation', 'Error while canceling Reservation');
        return;
      } finally {
        this.loadReservationById();
      }
    });
  }

  async cancelReservation(reservationId: string, cancelDate: Date): Promise<boolean> {
    const response = await this.reservationService.cancelReservation(reservationId, cancelDate);
    return response?.data?.status == 'Cancelled';
  }

  async updateReservationInvoiceStatusAfterCancelation(reservationId: string): Promise<boolean> {
    const response = await this.reservationService.updateReservationInvoiceStatusAfterCancelation(reservationId);
    const {
      exclude_from_invoice,
      verification_paid
    } = response?.data || {};

    return exclude_from_invoice && verification_paid;
  }

  onUncancelReservationClick() {
    const statusDialogRef = this.dialogRef.open(UpdateReservationStatusDialogComponent, {
      width: '750px',
      maxHeight: '400px',
      disableClose: true,
      data: [ this.reservation ]
    });
    statusDialogRef.afterClosed().subscribe(statusPayload => {
      if (statusPayload.statusId > 0) {
        try {
          const updatePayload = this.buildUpdatePayload(statusPayload, this.reservation);
          const infoPayload = this.buildInfoPayload(this.reservation);

          this.reservationService.updateReservationStatus(this.reservation, updatePayload).then(_ => {
            this.reservationService.updateReservationInfo(this.reservation, infoPayload).then(_ => {
              this.load();
            });
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while updating Reservation status', 'Error while updating Reservation status');
        }
      }
    });
  }

  buildUpdatePayload(statusPayload: any, reservation: any) {
    const updatePayload = {
      id: reservation.reservation_id.toString(),
      status_date: statusPayload.statusDate,
      status_id: statusPayload.statusId
    }
    return updatePayload;
  }

  buildInfoPayload(reservation: any) {
    const infoPayload = {
      exclude_from_invoice: 1,
      id: reservation.reservation_id.toString(),
      verification_paid: reservation.verification_paid
    }
    return infoPayload;
  }

  getReservationStatusColor(status: string): string {
    let reservationStatusColorProp: ColorPropType = ColorProp.ReservationStatusDefault;

    switch (status.toUpperCase()) {
      case 'UPCOMING':
        reservationStatusColorProp = ColorProp.ReservationStatusUpcoming;
        break;
      case 'IN HOME':
        reservationStatusColorProp = ColorProp.ReservationStatusInHome;
        break;
      case 'COMPLETE':
        reservationStatusColorProp = ColorProp.ReservationStatusComplete;
        break;
      case 'CLAIM FILED':
        reservationStatusColorProp = ColorProp.ReservationStatusClaimFiled;
        break;
      case 'CANCELED':
        reservationStatusColorProp = ColorProp.ReservationStatusCanceled;
        break;
    }

    return getColor(reservationStatusColorProp);
  }

  goToClaimTokenLink() {
    let token_link = document.createElement('a');
    token_link.href = `https://claim.safely.com/?claim_token=${this.reservation.claim_token}`;
    token_link.click();
  }
}